/**
 * Floor plan model (mirrors services/api/src/floorplan.ts) + pure layout helpers for the 3D floor and planner.
 * Metres on a room-local plane: x across, z deep, centre (0,0); rot in degrees.
 */
export type RoomKind = 'indoor' | 'terrace' | 'rooftop' | 'garden';
export type FloorStyle = 'wood' | 'wood-dark' | 'tile' | 'terrazzo' | 'concrete' | 'grass' | 'deck';
export type TableShape = 'round' | 'square' | 'long' | 'booth' | 'bar';
export type PropKind =
  | 'wall' | 'window' | 'door' | 'pillar' | 'stairs'
  | 'counter' | 'bar' | 'kitchen' | 'cashier' | 'sofa' | 'divider'
  | 'plant' | 'tree' | 'umbrella' | 'lamp' | 'rug' | 'fridge' | 'tv'
  | 'picture' | 'neon' | 'shelf' | 'ac' | 'clock' | 'fan' | 'pendant'
  | 'shrine' | 'cart' | 'aquarium' | 'bamboo' | 'menuboard' | 'water' | 'flowers';

export interface Room { id: string; name: string; kind: RoomKind; w: number; d: number; floor: FloorStyle; wall: string; level: number }
export interface Prop { id: string; room: string; kind: PropKind; x: number; z: number; rot: number; w?: number; d?: number; color?: string; text?: string }
export interface Placement { room: string; x: number; z: number; rot: number; shape: TableShape; color?: string }
export interface FloorPlan { rooms: Room[]; props: Prop[]; tables: Record<string, Placement>; rev: number }
export interface TableRef { id: string; name: string; seats: number }

type T = { th: string; en: string };
export const ROOM_KINDS: Record<RoomKind, T> = {
  indoor: { th: 'ในร้าน', en: 'Indoor' }, terrace: { th: 'ระเบียง', en: 'Terrace' },
  rooftop: { th: 'ดาดฟ้า', en: 'Rooftop' }, garden: { th: 'สวน', en: 'Garden' },
};
export const FLOOR_STYLES: Record<FloorStyle, T & { base: string }> = {
  wood: { th: 'ไม้', en: 'Wood', base: '#C99A64' }, 'wood-dark': { th: 'ไม้เข้ม', en: 'Dark wood', base: '#7A5134' },
  tile: { th: 'กระเบื้อง', en: 'Tile', base: '#E8E2D6' }, terrazzo: { th: 'หินขัด', en: 'Terrazzo', base: '#D9D3C7' },
  concrete: { th: 'ปูน', en: 'Concrete', base: '#A9A9A4' }, grass: { th: 'หญ้า', en: 'Grass', base: '#78B05A' },
  deck: { th: 'พื้นไม้ระเบียง', en: 'Deck', base: '#A8724A' },
};
export const WALL_COLORS = ['#E9DFCB', '#F4F1EA', '#F2C9A0', '#BFD8C4', '#BBD3FF', '#2E3550', '#C46A4C'];

export const TABLE_SHAPES: Record<TableShape, T & { w: number; d: number; seats: number }> = {
  round: { th: 'โต๊ะกลม', en: 'Round', w: 1.1, d: 1.1, seats: 4 },
  square: { th: 'โต๊ะเหลี่ยม', en: 'Square', w: 0.9, d: 0.9, seats: 4 },
  long: { th: 'โต๊ะยาว', en: 'Long', w: 2.0, d: 0.9, seats: 6 },
  booth: { th: 'บูธโซฟา', en: 'Booth', w: 1.4, d: 1.7, seats: 4 },
  bar: { th: 'โต๊ะบาร์', en: 'Bar table', w: 0.7, d: 0.7, seats: 2 },
};

export type PropGroup = 'build' | 'furniture' | 'decor';
/**
 * Pieces the owner can place. `mount`: hangs on a wall (snaps to the nearest wall, faces into the room, only
 * bumps into other wall pieces). `overhead`: hangs from the ceiling and never blocks the floor. `solid`: blocks
 * tables and other solid floor pieces. `text`: carries a short line of text (neon sign, menu board).
 */
export interface PropSpec extends T { group: PropGroup; w: number; d: number; sized?: boolean; solid: boolean; mount?: boolean; overhead?: boolean; text?: boolean }
export const PROPS: Record<PropKind, PropSpec> = {
  wall: { th: 'ผนัง', en: 'Wall', group: 'build', w: 3, d: 0.15, sized: true, solid: true },
  window: { th: 'หน้าต่าง', en: 'Window', group: 'build', w: 2, d: 0.15, sized: true, solid: false, mount: true },
  door: { th: 'ประตู', en: 'Door', group: 'build', w: 1.1, d: 0.2, solid: false, mount: true },
  pillar: { th: 'เสา', en: 'Pillar', group: 'build', w: 0.45, d: 0.45, solid: true },
  stairs: { th: 'บันได', en: 'Stairs', group: 'build', w: 1.2, d: 2.6, sized: true, solid: true },
  ac: { th: 'แอร์', en: 'Air-con', group: 'build', w: 1.0, d: 0.25, solid: false, mount: true },
  fan: { th: 'พัดลมเพดาน', en: 'Ceiling fan', group: 'build', w: 1.4, d: 1.4, solid: false, overhead: true },
  counter: { th: 'เคาน์เตอร์', en: 'Counter', group: 'furniture', w: 3, d: 0.8, sized: true, solid: true },
  bar: { th: 'บาร์', en: 'Bar', group: 'furniture', w: 3, d: 0.7, sized: true, solid: true },
  kitchen: { th: 'ครัว', en: 'Kitchen', group: 'furniture', w: 3.5, d: 1.4, sized: true, solid: true },
  cashier: { th: 'แคชเชียร์', en: 'Cashier', group: 'furniture', w: 1.4, d: 0.7, solid: true },
  sofa: { th: 'โซฟา', en: 'Sofa', group: 'furniture', w: 2, d: 0.8, sized: true, solid: true },
  divider: { th: 'กระถางกั้น', en: 'Planter wall', group: 'furniture', w: 2, d: 0.4, sized: true, solid: true },
  fridge: { th: 'ตู้แช่', en: 'Drinks fridge', group: 'furniture', w: 0.8, d: 0.7, solid: true },
  shelf: { th: 'ชั้นวาง', en: 'Wall shelf', group: 'furniture', w: 1.4, d: 0.3, sized: true, solid: false, mount: true },
  cart: { th: 'รถเข็น', en: 'Food cart', group: 'furniture', w: 1.6, d: 0.8, solid: true },
  aquarium: { th: 'ตู้ปลา', en: 'Aquarium', group: 'furniture', w: 1.2, d: 0.5, sized: true, solid: true },
  water: { th: 'ตู้กดน้ำ', en: 'Water cooler', group: 'furniture', w: 0.45, d: 0.45, solid: true },
  menuboard: { th: 'ป้ายเมนู', en: 'Menu board', group: 'furniture', w: 0.7, d: 0.5, solid: true, text: true },
  plant: { th: 'ต้นไม้', en: 'Plant', group: 'decor', w: 0.6, d: 0.6, solid: true },
  tree: { th: 'ต้นไม้ใหญ่', en: 'Tree', group: 'decor', w: 1.4, d: 1.4, solid: true },
  bamboo: { th: 'ไผ่', en: 'Bamboo', group: 'decor', w: 0.6, d: 0.6, solid: true },
  flowers: { th: 'กระถางดอกไม้', en: 'Flowers', group: 'decor', w: 0.5, d: 0.5, solid: true },
  umbrella: { th: 'ร่ม', en: 'Umbrella', group: 'decor', w: 0.4, d: 0.4, solid: false },
  lamp: { th: 'โคมไฟ', en: 'Lamp', group: 'decor', w: 0.4, d: 0.4, solid: true },
  pendant: { th: 'โคมแขวน', en: 'Pendant light', group: 'decor', w: 0.5, d: 0.5, solid: false, overhead: true },
  rug: { th: 'พรม', en: 'Rug', group: 'decor', w: 2.4, d: 1.6, sized: true, solid: false },
  tv: { th: 'ทีวี', en: 'TV', group: 'decor', w: 1.4, d: 0.3, solid: false, mount: true },
  picture: { th: 'รูปติดผนัง', en: 'Wall art', group: 'decor', w: 1.0, d: 0.08, sized: true, solid: false, mount: true },
  neon: { th: 'ป้ายไฟนีออน', en: 'Neon sign', group: 'decor', w: 1.8, d: 0.1, sized: true, solid: false, mount: true, text: true },
  clock: { th: 'นาฬิกา', en: 'Clock', group: 'decor', w: 0.45, d: 0.08, solid: false, mount: true },
  shrine: { th: 'ศาลพระภูมิ', en: 'Spirit house', group: 'decor', w: 0.9, d: 0.9, solid: true },
};
export const TEXT_MAX = 24;

export const GRID = 0.25;
export const snap = (v: number, step = GRID) => Math.round(v / step) * step;
export const round2 = (v: number) => Math.round(v * 100) / 100;

/** Axis-aligned box a thing covers after rotation (any angle: the rotated rectangle's bounds). */
export function footprint(x: number, z: number, w: number, d: number, rot: number) {
  const a = (rot * Math.PI) / 180;
  const c = Math.abs(Math.cos(a)), s = Math.abs(Math.sin(a));
  const hw = (w * c + d * s) / 2, hd = (w * s + d * c) / 2;
  return { x0: x - hw, x1: x + hw, z0: z - hd, z1: z + hd };
}
type Box = ReturnType<typeof footprint>;
const overlap = (a: Box, b: Box, gap: number) => a.x0 < b.x1 + gap && b.x0 < a.x1 + gap && a.z0 < b.z1 + gap && b.z0 < a.z1 + gap;

/** Table footprint includes its chairs (0.45 m around), so tables never end up chair-in-chair. */
export function tableBox(p: Placement) {
  const s = TABLE_SHAPES[p.shape];
  const pad = p.shape === 'booth' ? 0 : 0.45;
  return footprint(p.x, p.z, s.w + pad * 2, s.d + pad * 2, p.rot);
}
export function propBox(p: Prop) {
  const c = PROPS[p.kind];
  return footprint(p.x, p.z, p.w ?? c.w, p.d ?? c.d, p.rot);
}

export type Thing = { type: 'table'; id: string; p: Placement } | { type: 'prop'; id: string; p: Prop };

type Layer = 'floor' | 'mount' | 'overhead' | 'none';
/** What a thing can bump into: floor things (tables, solid pieces), wall pieces, or nothing. */
function layerOf(t: Thing): Layer {
  if (t.type === 'table') return 'floor';
  const c = PROPS[t.p.kind];
  return c.mount ? 'mount' : c.overhead ? 'overhead' : c.solid ? 'floor' : 'none';
}

/**
 * Can this thing stand here? Inside the room (wall pieces sit on the wall line) and not overlapping anything on
 * its own layer: tables and solid floor pieces block each other, wall pieces only block other wall pieces, and
 * rugs, umbrellas and ceiling pieces never block. `ignore` = the thing being moved.
 */
export function fits(plan: FloorPlan, room: Room, t: Thing, ignore?: string): boolean {
  const box = t.type === 'table' ? tableBox(t.p) : propBox(t.p);
  const layer = layerOf(t);
  const slack = layer === 'mount' ? 0.3 : 0.001;
  if (box.x0 < -room.w / 2 - slack || box.x1 > room.w / 2 + slack || box.z0 < -room.d / 2 - slack || box.z1 > room.d / 2 + slack) return false;
  if (layer === 'none' || layer === 'overhead') return true;
  if (layer === 'floor') {
    for (const [id, p] of Object.entries(plan.tables)) {
      if (id === ignore || p.room !== room.id) continue;
      if (overlap(box, tableBox(p), 0)) return false;
    }
  }
  for (const p of plan.props) {
    if (p.id === ignore || p.room !== room.id) continue;
    if (layerOf({ type: 'prop', id: p.id, p }) !== layer) continue;
    if (overlap(box, propBox(p), -0.02)) return false;
  }
  return true;
}

/**
 * A wall piece goes on the wall nearest to (x, z), facing into the room. rot follows the scene's convention:
 * 0 = on the back wall (-z), 180 = front (+z), 270 = left (-x), 90 = right (+x).
 */
export function snapToWall(room: Room, kind: PropKind, x: number, z: number, w?: number): { x: number; z: number; rot: number } {
  const len = w ?? PROPS[kind].w;
  const along = (v: number, size: number) => snap(Math.max(-size / 2 + len / 2, Math.min(size / 2 - len / 2, v)));
  const d = [z + room.d / 2, room.d / 2 - z, x + room.w / 2, room.w / 2 - x];
  const i = d.indexOf(Math.min(...d));
  if (i === 0) return { x: along(x, room.w), z: -room.d / 2, rot: 0 };
  if (i === 1) return { x: along(x, room.w), z: room.d / 2, rot: 180 };
  if (i === 2) return { x: -room.w / 2, z: along(z, room.d), rot: 270 };
  return { x: room.w / 2, z: along(z, room.d), rot: 90 };
}

/** Smallest table number not taken yet ("1", "2", ...), for a freshly planted table. */
export function nextTableName(names: string[]): string {
  const used = new Set(names.map((n) => n.trim()));
  for (let i = 1; ; i++) if (!used.has(String(i))) return String(i);
}

export const newId = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);

/**
 * A first plan from the tables the restaurant already has: one indoor room, tables in rows, a kitchen, cashier,
 * door and window, the restaurant's name in neon, a fan and a few plants. The owner reshapes it from there.
 */
export function starterPlan(tables: TableRef[], name = 'FirstMango'): FloorPlan {
  const n = Math.max(1, tables.length);
  const cols = Math.min(6, Math.max(2, Math.ceil(Math.sqrt(n * 1.5))));
  const rows = Math.ceil(n / cols);
  const step = 2.75;
  const w = Math.max(9, round2(cols * step + 2));
  const d = Math.max(8, round2(rows * step + 4));
  const room: Room = { id: 'f1', name: 'ชั้น 1 · 1st floor', kind: 'indoor', w, d, floor: 'wood', wall: WALL_COLORS[0], level: 1 };
  const placed: Record<string, Placement> = {};
  tables.forEach((t, i) => {
    const c = i % cols, r = Math.floor(i / cols);
    placed[t.id] = { room: room.id, x: snap(-((cols - 1) * step) / 2 + c * step), z: snap(-d / 2 + 3.4 + r * step), rot: 0, shape: t.seats > 4 ? 'long' : 'square' };
  });
  const id = room.id;
  const props: Prop[] = [
    { id: 'kitchen1', room: id, kind: 'kitchen', x: snap(-w / 2 + 2.25), z: snap(-d / 2 + 0.85), rot: 0 },
    { id: 'cashier1', room: id, kind: 'cashier', x: snap(w / 2 - 1.5), z: snap(-d / 2 + 0.7), rot: 0 },
    { id: 'door1', room: id, kind: 'door', x: snap(w / 2 - 1.8), z: d / 2, rot: 180 },
    { id: 'window1', room: id, kind: 'window', x: -w / 2, z: 0, rot: 270, w: Math.min(3, snap(d - 3)) },
    { id: 'neon1', room: id, kind: 'neon', x: snap(w / 2 - 3.8), z: -d / 2, rot: 0, text: name.slice(0, TEXT_MAX) },
    { id: 'ac1', room: id, kind: 'ac', x: w / 2, z: snap(-d / 2 + 2.5), rot: 90 },
    { id: 'fan1', room: id, kind: 'fan', x: 0, z: snap(-d / 2 + 3.4 + ((rows - 1) * step) / 2), rot: 0 },
    { id: 'menuboard1', room: id, kind: 'menuboard', x: snap(w / 2 - 0.6), z: snap(d / 2 - 0.6), rot: 200, text: 'วันนี้ · Today' },
    { id: 'plant1', room: id, kind: 'plant', x: snap(-w / 2 + 0.5), z: snap(d / 2 - 0.5), rot: 0 },
    { id: 'plant2', room: id, kind: 'plant', x: snap(w / 2 - 0.5), z: snap(d / 2 - 1.75), rot: 0 },
  ];
  return { rooms: [room], props, tables: placed, rev: 0 };
}

/** Where each room sits in the "all rooms" overview: side by side in floor order with a gap. */
export function roomOrigins(rooms: Room[], gap = 3): Map<string, { x: number; z: number }> {
  const sorted = [...rooms].sort((a, b) => a.level - b.level || rooms.indexOf(a) - rooms.indexOf(b));
  const total = sorted.reduce((s, r) => s + r.w, 0) + gap * (sorted.length - 1);
  let x = -total / 2;
  const out = new Map<string, { x: number; z: number }>();
  for (const r of sorted) { out.set(r.id, { x: x + r.w / 2, z: 0 }); x += r.w + gap; }
  return out;
}

export const unplaced = (plan: FloorPlan, tables: TableRef[]) => tables.filter((t) => !plan.tables[t.id] || !plan.rooms.some((r) => r.id === plan.tables[t.id].room));
